import React, { type RefAttributes, Component } from 'react';
import type { Dispatch } from 'redux';
import { styled } from '@compiled/react';
import Button, { type ButtonProps } from '@atlaskit/button';
import DropdownMenu, {
	DropdownItemGroup,
	DropdownItem,
	type CustomTriggerProps,
} from '@atlaskit/dropdown-menu';
import { Field } from '@atlaskit/form';
import LockLockedIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import LockUnlockedIcon from '@atlaskit/icon/core/migration/lock-unlocked--unlock';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import PeopleIcon from '@atlaskit/icon/glyph/people';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireUIAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { connect } from '@atlassian/jira-react-redux';
import { FREE_ACCESS_LEVEL } from '@atlassian/jira-shared-types';
import {
	type Action,
	fetchProjectAccessTypes,
	setProjectAccessType,
	fetchProjectAccessValue,
} from '../../action-creators';
import {
	accessTypesSelector,
	isLoadingSelector,
	hasFetchDataErrorSelector,
	selectedAccessTypeSelector,
} from '../../selectors';
import type { AccessType, State } from '../../types';
import ConnectedDisabledAccessLevel from './disabled-access-level';
import messages from './messages';
import { ProjectAccessSelect } from './project-access-field-view-select-field';
import { AccessWrapper, DescriptionField, ICON_SIZE } from './styled';
import type {
	DispatchProps,
	DropdownWidthStyles,
	OwnProps,
	PropsWithIntl,
	ReactComponentState,
	StateProps,
} from './types';

export const extractCurrentAccessTypeFromAvailableOptions = (
	availableOptions: AccessType[],
	currentAccessType?: AccessType,
): AccessType | null | undefined => {
	if (availableOptions && availableOptions.length && currentAccessType) {
		const curAccessType: AccessType = currentAccessType;

		const selectedValues = availableOptions.find((type) => curAccessType.value === type.value);
		if (selectedValues) {
			return selectedValues;
		}
		return currentAccessType;
	}

	return undefined;
};
export const getIsDowngraded = (currentSelect?: AccessType | null): boolean =>
	(currentSelect && currentSelect.value) !== FREE_ACCESS_LEVEL;

// eslint-disable-next-line jira/react/no-class-components
export class ProjectAccessOld extends Component<PropsWithIntl, ReactComponentState> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static defaultProps = {
		validateRequired: false,
		requiredAccess: false,
	};

	static getIcon(icon: string, _label = '', noColour = false) {
		const localProps = {
			label: '',
			color: noColour ? undefined : token('color.icon.subtle', colors.N100),
			LEGACY_size: ICON_SIZE,
			spacing: 'spacious' as const,
		};

		switch (icon) {
			case 'open':
				return <LockUnlockedIcon {...localProps} />;
			case 'limited':
				return (
					<PeopleIcon
						label={localProps.label}
						primaryColor={localProps.color}
						size={localProps.LEGACY_size}
					/>
				);
			case 'private':
				return <LockLockedIcon {...localProps} />;
			default:
				return (
					<PeopleIcon
						label={localProps.label}
						primaryColor={localProps.color}
						size={localProps.LEGACY_size}
					/>
				);
		}
	}

	state = {
		// This isOpen state doesn't control whether the dropdown is actually open.
		// The dropdown manages its own state.
		// This is only used to change styles depending on the dropdown's reported state.
		isOpen: false,
	};

	UNSAFE_componentWillMount() {
		this.props.onWillMount();
	}

	componentDidUpdate(prevProps: PropsWithIntl) {
		if (this.props.onError && this.props.hasFetchDataError) {
			this.props.onError();
		}
		if (
			this.props.onAccessTypesLoaded &&
			prevProps.accessTypes.length === 0 &&
			this.props.accessTypes.length > 0
		) {
			this.props.onAccessTypesLoaded();
		}
		if (this.props.currentAccessType && !prevProps.currentAccessType) {
			this.props.onFirstChange(this.props.currentAccessType.value);
		}
	}

	getButtonTheme = (currentTheme: unknown, props: unknown) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		const { buttonStyles, ...rest } = currentTheme(props);
		const { currentAccessType, validateRequired } = this.props;

		return {
			...rest,
			buttonStyles: {
				...buttonStyles,
				border:
					!currentAccessType && validateRequired
						? `2px solid ${token('color.border.danger', colors.R400)}`
						: 'none',
				textAlign: 'left',
				lineHeight: '3em',
			},
		};
	};

	getButtonThemeOld = (currentTheme: unknown, props: unknown) => {
		// @ts-expect-error - TS2571 - Object is of type 'unknown'.
		const { buttonStyles, ...rest } = currentTheme(props);
		const { currentAccessType, validateRequired } = this.props;

		return {
			...rest,
			buttonStyles: {
				...buttonStyles,
				border:
					!currentAccessType && validateRequired
						? `2px solid ${token('color.border.danger', colors.R400)}`
						: 'none',
				textAlign: 'left',
			},
		};
	};

	getDropdownMenu(currentSelect?: AccessType | null) {
		const {
			accessTypes,
			isLoading,
			isDisabled,
			intl: { formatMessage },
			dropdownWidthStyles,
			currentAccessType,
			validateRequired,
		} = this.props;
		const { isOpen } = this.state;

		return (
			<DropdownMenu
				testId="project-access.field.dropdown-menu"
				trigger={({ triggerRef, ...props }: CustomTriggerProps) => (
					<TriggerButton
						{...props}
						shouldFitContainer
						iconBefore={
							currentSelect && currentSelect.displayName
								? ProjectAccessOld.getIcon(
										currentSelect.iconName,
										currentSelect.displayName,
										isOpen,
									)
								: undefined
						}
						iconAfter={<ChevronDownIcon label="" size="medium" />}
						isDisabled={isDisabled}
						isInvalid={!currentAccessType && validateRequired}
						testId="project-access.field.button-trigger"
						aria-expanded={isOpen ? 'true' : 'false'}
						ref={triggerRef}
					>
						{currentSelect && currentSelect.displayName
							? currentSelect.displayName
							: formatMessage(messages.projectAccessPlaceholder)}
					</TriggerButton>
				)}
				shouldFlip
				onOpenChange={this.handleOpenChange}
				isLoading={isLoading}
			>
				<DropdownWrapper widthStyles={dropdownWidthStyles}>
					<DropdownItemGroup>
						{accessTypes.map((type: AccessType) => (
							<DropdownItem
								onClick={() => this.props.onChange(type)}
								key={type.value}
								shouldTitleWrap
								shouldDescriptionWrap
								elemBefore={ProjectAccessOld.getIcon(type.iconName, type.displayName)}
							>
								<div data-testid="project-access.field.option">{type.displayName}</div>
								<DescriptionField>
									{/* eslint-disable react/no-danger */}
									<span dangerouslySetInnerHTML={{ __html: type.description }} />
								</DescriptionField>
							</DropdownItem>
						))}
					</DropdownItemGroup>
				</DropdownWrapper>
			</DropdownMenu>
		);
	}

	getSelected = (): AccessType | null | undefined => {
		const { accessTypes, currentAccessType } = this.props;

		return extractCurrentAccessTypeFromAvailableOptions(accessTypes, currentAccessType);
	};

	handleOpenChange = (data: { readonly isOpen: boolean }) => {
		this.props.onOpenChange?.(data.isOpen);
		this.setState({ isOpen: data.isOpen });
	};

	// changing currentSelect  to optional because in new design by default currentSelect is null
	renderAccess = (currentSelect?: AccessType | null) => {
		const {
			DisabledAccessLevel,
			isDisabled,
			intl: { formatMessage },
		} = this.props;
		let isDowngraded = false;
		if (!isDisabled) {
			return this.getDropdownMenu(currentSelect);
		}

		// Downgraded projects keep their original access level, but cannot be modified
		isDowngraded = getIsDowngraded(currentSelect);

		if (isDowngraded) {
			return (
				<ContextualAnalyticsData sourceName="projectDetails" sourceType={SCREEN}>
					<Tooltip
						content={formatMessage(messages.downgradedProjectAccessMessage)}
						onShow={(event) => {
							fireUIAnalytics(event, 'accessLevelFreeTier');
						}}
					>
						{this.getDropdownMenu(currentSelect)}
					</Tooltip>
				</ContextualAnalyticsData>
			);
		}

		// Non-downgraded projects will display a special message.
		return (
			<ContextualAnalyticsData sourceName="projectDetailsDisabledAccessLevel" sourceType={SCREEN}>
				<FireScreenAnalytics />
				<DisabledAccessLevel />
			</ContextualAnalyticsData>
		);
	};

	setAccessForFreeEdition = (currentSelect: AccessType) => {
		if (!getIsDowngraded(currentSelect)) {
			this.props.onChange(currentSelect);
		}
	};

	render() {
		const {
			intl: { formatMessage },
			requiredAccess,
			isLoading,
			accessTypes,
		} = this.props;
		const currentSelect = this.getSelected();
		if (currentSelect) {
			this.setAccessForFreeEdition(currentSelect);
		}

		return isLoading || accessTypes.length > 0 ? (
			<AccessWrapper data-pollinator-access-level-picker data-testid="project-access.field.wrapper">
				<Field
					name="project-access-field"
					isRequired={getIsDowngraded(currentSelect) ? requiredAccess : false}
					label={formatMessage(messages.projectAccessTitle)}
				>
					{() => this.renderAccess(currentSelect)}
				</Field>
			</AccessWrapper>
		) : null;
	}
}

const mapStateToProps = (state: State): StateProps => ({
	accessTypes: accessTypesSelector(state),
	currentAccessType: selectedAccessTypeSelector(state),
	isLoading: isLoadingSelector(state),
	hasFetchDataError: hasFetchDataErrorSelector(state),
	DisabledAccessLevel: ConnectedDisabledAccessLevel,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps): DispatchProps => ({
	onChange: (accessType: AccessType) => {
		dispatch(setProjectAccessType(accessType));
		ownProps.onChange?.(accessType.value);
	},
	onWillMount: () => {
		dispatch(fetchProjectAccessTypes());
		dispatch(fetchProjectAccessValue());
	},
});

export const ProjectAccess = ProjectAccessSelect;

// @ts-expect-error - Types of property 'onChange' are incompatible.
export default connect(mapStateToProps, mapDispatchToProps)(ProjectAccessSelect);

type TriggerButtonProps = { isInvalid?: boolean } & ButtonProps & RefAttributes<HTMLElement>;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerButton = styled<TriggerButtonProps>(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		textAlign: 'left',
		height: '3em',
		lineHeight: '3em',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		border: ({ isInvalid }: TriggerButtonProps) =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			isInvalid ? `2px solid ${token('color.border.danger', colors.R400)}` : 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownWrapper = styled.div<{ widthStyles?: DropdownWidthStyles }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ widthStyles }) => widthStyles?.width,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	minWidth: ({ widthStyles }) => widthStyles?.minWidth,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ widthStyles }) => widthStyles?.maxWidth,
});
